/**
 * Desplegable propio. No usa <select> nativo por preferencia del proyecto:
 * el nativo no se puede estilar de forma consistente entre navegadores y en
 * Windows abre un menu del sistema que rompe el aspecto de la aplicacion.
 *
 * Se abre pulsando cualquier parte del control, no solo una flechita.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLang } from '../i18n.jsx';

/**
 * A partir de cuantas opciones aparece el buscador.
 *
 * Por debajo se leen de un vistazo y un campo de texto solo estorbaria. Por
 * encima hay que desplazarse, y ahi buscar es mas rapido que mirar: la lista de
 * arquetipos del meta pasa de noventa.
 */
const UMBRAL_BUSCADOR = 10;

/**
 * Normaliza para comparar: sin acentos y en minusculas.
 *
 * Escribir "pokegear" tiene que encontrar "Pokégear". Es el mismo criterio que
 * usa la base de datos para buscar cartas, asi que la aplicacion se comporta
 * igual en los dos sitios.
 */
const normaliza = (s) =>
  String(s ?? '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

export default function Dropdown({ label, value, options, onChange, placeholder, porDefecto = null }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useId();

  const selected = options.find((o) => o.key === value) ?? null;
  const conBuscador = options.length > UMBRAL_BUSCADOR;

  /*
   * Se busca en CUALQUIER parte del nombre, no solo al principio.
   *
   * Los arquetipos vienen como "Marnie's Grimmsnarl" o "Iono's Bellibolt": lo
   * que uno tiene en la cabeza es el Pokemon, que va al final. Con coincidencia
   * por prefijo habria que saberse de memoria el entrenador que lo acompaña.
   */
  const visibles = useMemo(() => {
    const q = normaliza(busqueda).trim();
    if (!q) return options;
    return options.filter((o) => normaliza(o.label).includes(q));
  }, [options, busqueda]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      // Al cerrar se limpia: reabrir con el filtro de la vez anterior puesto se
      // lee como que faltan opciones.
      setBusqueda('');
      return;
    }
    setHighlight(Math.max(0, visibles.findIndex((o) => o.key === value)));
    // El foco va al buscador si lo hay, para poder escribir sin otro clic.
    if (conBuscador) inputRef.current?.focus();
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Al cambiar el filtro, el resaltado vuelve arriba: si no, apuntaria a una
  // posicion que ya no existe y Enter elegiria algo que no se ve.
  useEffect(() => setHighlight(0), [busqueda]);

  const commit = (key) => {
    onChange(key);
    setOpen(false);
    setBusqueda('');
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, visibles.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Home' || e.key === 'End') {
      // En el buscador, Home/End mueven el cursor del texto: ahi no se tocan.
      if (e.target === inputRef.current) return;
      e.preventDefault();
      setHighlight(e.key === 'Home' ? 0 : visibles.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (visibles.length) commit(visibles[highlight]?.key ?? null);
    }
  };

  return (
    /* `is-filtrado` marca que hay un valor elegido DE VERDAD: la opcion
       "cualquiera" tiene clave nula y tambien casa en `selected`, asi que se
       exige clave no nula. El CSS decide donde el estado merece señal.
       `porDefecto` es la clave con la que el control NACE (Standard en
       Cartas): pintarla filtrada decía «has acotado algo» a quien no tocó
       nada, y el filo de color dejaba de significar lo que significa. */
    <div
      className={`dropdown${selected?.key != null && selected.key !== porDefecto ? ' is-filtrado' : ''}`}
      ref={rootRef}
    >
      <span className="dropdown__label">{label}</span>
      <button
        type="button"
        className={`dropdown__control${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
      >
        {/* El texto se recorta AQUI, no en el contenedor.
            Recortarlo fuera obliga a `overflow: hidden` en la celda, y eso se
            come tambien la lista desplegada, que es absoluta. */}
        <span className={`dropdown__value${selected ? '' : ' dropdown__placeholder'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg viewBox="0 0 12 8" aria-hidden="true" className="dropdown__chevron">
          <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      </button>

      {/*
        La lista se queda montada y solo cambia de clase. Desmontarla al cerrar
        seria mas limpio, pero entonces no hay nodo al que animar y el
        desplegable se esfumaria de golpe.

        `inert` es lo que hace que esto no rompa la accesibilidad: mientras esta
        cerrada, la lista no recibe foco ni la anuncia el lector de pantalla,
        aunque siga en el DOM. Sin el, tabular desde el control saltaria a
        opciones invisibles.

        Y TODOS sus <li> van con `role="presentation"`. Un `listbox` tiene que
        tener hijos `option`, pero entre medias habia un <li> -- que para un
        lector es un `listitem` --, asi que los botones con `role=option` NO
        contaban como opciones de esta lista: la relacion estaba partida.
        Lighthouse lo marcaba como ARIA mal usado para el rol, y salia en todas
        las pantallas porque este componente esta en todas. `presentation` saca
        el <li> del arbol de accesibilidad sin tocar el HTML ni el CSS: los
        botones pasan a ser hijos directos del listbox, que es lo que ya
        parecian.
      */}
      <ul
        className={`dropdown__list${open ? ' is-open' : ''}`}
        id={listId}
        role="listbox"
        inert={!open}
      >
        {conBuscador && (
          /* Pegado arriba: la lista se desplaza por dentro y el buscador tiene
             que seguir a la vista mientras se mira el final. */
          <li className="dropdown__search" role="presentation">
            <input
              ref={inputRef}
              type="search"
              value={busqueda}
              placeholder={t('filterOptions')}
              aria-label={t('filterOptions')}
              autoComplete="off"
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={onKeyDown}
            />
          </li>
        )}

        {visibles.length === 0 ? (
          <li className="dropdown__empty" role="presentation">{t('noOptions')}</li>
        ) : (
          visibles.map((o, i) => (
            <li key={o.key ?? '__any'} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={o.key === value}
                className={
                  'dropdown__option' +
                  (o.key === value ? ' is-selected' : '') +
                  (i === highlight ? ' is-highlighted' : '')
                }
                onMouseEnter={() => setHighlight(i)}
                onClick={() => commit(o.key)}
              >
                {o.extra != null ? (
                  /* Dato de apoyo a la derecha de la opcion (el record de un
                     mazo en "Por mazo"). El buscador solo mira `label`: el
                     numero acompaña, no se busca por el. */
                  <>
                    <span className="dropdown__optTexto">{o.label}</span>
                    <span className="dropdown__optExtra">{o.extra}</span>
                  </>
                ) : (
                  o.label
                )}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
